import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { qs } from "./query";

export type NumberSeries = Datified<Schemas["NumberSeries"], "created_at" | "updated_at">;
export type NumberSeriesCreate = Schemas["NumberSeriesCreate"];
export type NumberSeriesUpdate = Schemas["NumberSeriesUpdate"];
export type NumberPreview = Schemas["NumberPreview"];

/** A company's number series, newest year first; year undefined = all. */
export function useNumberSeries(companyId: string, year?: number) {
  return useQuery<NumberSeries[]>({
    queryKey: ["companies", companyId, "series", year ?? "all"],
    queryFn: async () =>
      (await apiGet<{ items: NumberSeries[] }>(`/companies/${companyId}/number-series${qs({ year })}`)).items,
  });
}

/** The number the next auto-numbered document would get (not reserved). */
export function useNextNumber(companyId: string, docType: string, year: number, enabled: boolean) {
  return useQuery<NumberPreview>({
    queryKey: ["companies", companyId, "next-number", docType, year],
    queryFn: () =>
      apiGet<NumberPreview>(`/companies/${companyId}/next-number${qs({ doc_type: docType, year })}`),
    enabled,
    retry: false,
    meta: { suppressErrorToast: true },
  });
}

function useInvalidate(companyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["companies", companyId, "series"] });
    void qc.invalidateQueries({ queryKey: ["companies", companyId, "next-number"] });
  };
}

export function useCreateSeries(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<NumberSeries, unknown, NumberSeriesCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<NumberSeries>(`/companies/${companyId}/number-series`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateSeries(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<NumberSeries, unknown, { id: string; input: NumberSeriesUpdate }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, input }) => apiPut<NumberSeries>(`/companies/${companyId}/number-series/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSeries(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiDelete(`/companies/${companyId}/number-series/${id}`);
    },
    onSuccess: invalidate,
  });
}
